import { Routes } from '@angular/router';
import { NeedLoginGuard } from 'src/app/common/need-login.guard';
import { ExternalSettingsResolver } from 'src/app/common/services/external-settings/external-settings-resolver.service';
import { RootComponent } from 'src/app/root/root.component';
import { SHARED_ROUTES } from './public.routes';
import { AppModeGuard } from './app-mode.guard';

export const PRIVATE_ROUTE_PATH = 'secured';

export const PRIVATE_ROUTES: Routes = [
  {
    path: PRIVATE_ROUTE_PATH,
    component: RootComponent,
    resolve: [ExternalSettingsResolver],
    canActivate: [NeedLoginGuard],
    canActivateChild: [AppModeGuard],
    children: [
      ...SHARED_ROUTES,
      {
        path: 'activity-reload',
        loadChildren: () => import('../../common/activity-reload/activity-reload.module').then((m) => m.ActivityReloadModule),
      },
      {
        path: 'account-history',
        loadChildren: () => import('../../business/account-history/account-history.module').then((m) => m.AccountHistoryModule),
        data: ["ACCOUNT_HISTORY.ACCOUNT_LIST", "LABEL_HISTORY"],
      },
      {
        path: 'account-history/check',
        loadChildren: () => import('../../business/account-history-check/account-history-check.module').then((m) => m.AccountHistoryCheckModule),
        data: ["ACCOUNT_HISTORY_ACCOUNT_LIST", "LABEL_HISTORY"]
      },
      {
        path: 'account-history/detail',
        loadChildren: () => import('../../business/account-history-detail/account-history-detail.module').then((m) => m.AccountHistoryDetailModule),
        data: ["ACCOUNT_HISTORY.ACCOUNT_LIST", "LABEL_HISTORY"]
      },
      {
        path: 'account-history/memo-post-detail',
        loadChildren: () => import('../../business/account-history-memo-post-detail/account-history-memo-post-detail.module').then((m) => m.AccountHistoryMemoPostDetailModule),
        data: ["ACCOUNT_HISTORY.ACCOUNT_LIST", "LABEL_HISTORY"]
      },
      {
        path: 'account-history/voucher-list',
        loadChildren: () => import('../../business/account-history-voucher-list/account-history-voucher-list.module').then((m) => m.AccountHistoryVoucherListModule),
        data: ["ACCOUNT_HISTORY_ACCOUNT_LIST", "LABEL_HISTORY"]
      },
      {
        path: 'account/floats',
        loadChildren: () => import('../../business/floats/floats.module').then((m) => m.FloatsModule),
        data: ["ACCOUNT_HISTORY_ACCOUNT_LIST", "ACCOUNT_FLOATS"]
      },
      {
        path: 'account/holds',
        loadChildren: () => import('../../business/holds/holds.module').then((m) => m.HoldsModule),
        data: ["ACCOUNT_HISTORY_ACCOUNT_LIST", "ACCOUNT_HOLDS"]
      },
      {
        path: 'available-activities',
        loadChildren: () => import('../../business/available-activities/available-activities.module').then((m) => m.AvailableActivitiesModule),
      },
      {
        path: 'batch-order-upload',
        loadChildren: () => import('../../business/batch-order-upload/batch-order-upload.module').then((m) => m.BatchOrderUploadModule),
        data: ["ORDERS", "BATCH_ORDER.TITLE"],
      },
      {
        path: 'batch-order-manage',
        loadChildren: () => import('../../business/batch-order-manage/batch-order-manage.module').then((m) => m.BatchOrderManageModule),
        data: ["ORDERS", "BATCH_ORDER.TITLE", "DETAILS"],
      },
      {
        path: 'biometrics',
        loadChildren: () => import('../../business/biometrics/biometrics.module').then((m) => m.BiometricsModule),
      },
      {
        path: 'cash',
        loadChildren: () => import('../../business/cash/cash.module').then((m) => m.CashModule),
        data: ["ORDERS", "ORDERS_CASH"],
      },
      {
        path: 'change-password/:reason',
        loadChildren: () => import('../../business/change-password/change-password.module').then((m) => m.ChangePasswordModule),
      },
      {
        path: 'cheque',
        loadChildren: () => import('../../business/cheque/cheque.module').then((m) => m.ChequeModule),
        data: ["ORDERS", "ORDERS_CHEQUE"],
      },
      {
        path: 'corporate/make-payment',
        loadChildren: () => import('../../business/corporate-make-payment/corporate-make-payment.module').then((m) => m.CorporateMakePaymentModule),
        data: ["LABEL_PAYMENTS", "LABEL_MAKE_A_PAYMENT"]
      },
      {
        path: 'corporate/payment-history',
        loadChildren: () => import('../../business/corporate-payment-history/corporate-payment-history.module').then((m) => m.CorporatePaymentHistoryModule),
        data: ["LABEL_PAYMENTS", "LABEL_PAYMENT_HISTORY"]
      },
      {
        path: 'corporate/manage-customer',
        loadChildren: () => import('../../business/corporate-manage-customer/corporate-manage-customer.module').then((m) => m.CorporateManageCustomerModule),
        data: ["LABEL_PAYMENTS", "LABEL_MANAGE_CUSTOMERS"]
      },
      {
        path: 'cross-currency-transfer',
        loadChildren: () => import('../../business/cross-currency-transfer/cross-currency-transfer.module').then((m) => m.CrossCurrencyTransferModule),
        data: ["LABEL_TRANSFERS", "LABEL_CROSS_CURRENCY_TRANSFER"],
      },
      {
        path: 'customer-information/:reason',
        loadChildren: () => import('../../business/cif-update/cif-update.module').then((m) => m.CifUpdateModule),
      },
      {
        path: 'dashboard',
        loadChildren: () => import('../../business/dashboard/dashboard.module').then((m) => m.DashboardModule),
        data: []
      },
      {
        path: 'demo/text',
        loadChildren: () => import('../../business/demo-text/demo-text.module').then((m) => m.DemoTextModule),
        data: []
      },
      {
        path: 'download-history',
        loadChildren: () => import('../../business/download-history/download-history.module').then((m) => m.DownloadHistoryModule),
        data: ["LABEL_ACCOUNTS", "LABEL_DOWNLOAD_HISTORY"],
      },
      {
        path: 'download-statement',
        loadChildren: () => import('../../business/download-statement/download-statement.module').then((m) => m.DownloadStatementModule),
        data: ["LABEL_ACCOUNTS", "LABEL_DOWNLOAD_STATEMENT"],
      },
      {
        path: 'draft',
        loadChildren: () => import('../../business/draft/draft.module').then((m) => m.DraftModule),
        data: ["ORDERS", "DRAFT_AUTOMATED"]
      },
      {
        path: 'draftAutomated',
        loadChildren: () => import('../../business/draft-automated/draft-automated.module').then((m) => m.DraftAutomatedModule),
        data: ["ORDERS", "DRAFT_AUTOMATED"]
      },
      {
        path: 'eft',
        loadChildren: () => import('../../business/eft/eft.module').then((m) => m.EftModule),
        data: ["ORDERS", "ORDERS_ELECTRONIC_FRANSFER"],
      },
      {
        path: 'external-links/:linkId',
        loadChildren: () => import('../../business/external-links/external-links.module').then((m) => m.ExternalLinksModule),
      },
      {
        path: 'external-transfer-history',
        loadChildren: () => import('../../business/order-history/order-history.module').then((m) => m.OrderHistoryModule),
        data: ["LABEL_EXTERNAL_TRANSFER", "LABEL_EXTERNAL_TRANSFER_HISTORY"],
      },
      {
        path: 'help-desk',
        loadChildren: () => import('../../business/help-desk/help-desk.module').then((m) => m.HelpDeskModule),
        data: ["LABEL_SUPPORT_CENTER", "SUPPORT_CENTER.TITLE", "SUPPORT_CENTER.HELP_DESK"]
      },
      {
        path: 'id-scan',
        loadChildren: () => import('../../business/id-scan/id-scan.module').then((m) => m.IdScanModule),
      },
      {
        path: 'internal-transfer',
        loadChildren: () => import('../../business/internal-transfer/internal-transfer.module').then((m) => m.InternalTransferModule),
        data: ["LABEL_TRANSFERS", "LABEL_INTERNAL_TRANSFER"],
      },
      {
        path: 'manage-payees',
        loadChildren: () => import('../../business/manage-payees/manage-payees.module').then((m) => m.ManagePayeesModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES"],
      },
      {
        path: 'manage-payees/add-peers',
        loadChildren: () => import('../../business/mp-add-peers/mp-add-peers.module').then((m) => m.MpAddPeersModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_MANAGE_ADD_A_PEER"],
      },
      {
        path: 'manage-payees/delete-peers',
        loadChildren: () => import('../../business/mp-delete-peers/mp-delete-peers.module').then((m) => m.MpDeletePeersModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_DELETE_PEER"],
      },
      {
        path: 'manage-payees/update-peers',
        loadChildren: () => import('../../business/mp-update-peers/mp-update-peers.module').then((m) => m.MpUpdatePeersModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_UPDATE_A_PEER"],
      },
      {
        path: 'manage-payees/add-merchants',
        loadChildren: () => import('../../business/mp-add-merchants/mp-add-merchants.module').then((m) => m.MpAddMerchantsModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_ADD_A_MERCHANT"],
      },
      {
        path: 'manage-payees/delete-merchants',
        loadChildren: () => import('../../business/mp-delete-merchants/mp-delete-merchants.module').then((m) => m.MpDeleteMerchantsModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_DELETE_A_MERCHANT"],
      },
      {
        path: 'manage-payees/update-merchants',
        loadChildren: () => import('../../business/mp-update-merchants/mp-update-merchants.module').then((m) => m.MpUpdateMerchantsModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_UPDATE_MERCHANT"],
      },
      {
        path: 'manage-payees/add-topup-recipients',
        loadChildren: () => import('../../business/mp-add-topup/mp-add-topup.module').then((m) => m.MpAddTopupModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_ADD_RECIPIENT"],
      },
      {
        path: 'manage-payees/delete-topup-recipients',
        loadChildren: () => import('../../business/mp-delete-topup/mp-delete-topup.module').then((m) => m.MpDeleteTopupModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_DELETE_A_RECIPIENT"],
      },
      {
        path: 'manage-payees/update-topup-recipients',
        loadChildren: () => import('../../business/mp-update-topup/mp-update-topup.module').then((m) => m.MpUpdateTopupModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_UPDATE_RECIPIENT"],
      },
      {
        path: 'manage-payees/add-external-payees',
        loadChildren: () => import('../../business/mp-add-external/mp-add-external.module').then((m) => m.MpAddExternalModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_ADD_AN_EXTERNAL_RECIPIENT"],
      },
      {
        path: 'manage-payees/delete-external-payees',
        loadChildren: () => import('../../business/mp-delete-external/mp-delete-external.module').then((m) => m.MpDeleteExternalModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_DELETE_AN_EXTERNAL_RECIPIENT"],
      },
      {
        path: 'manage-payees/update-external-payees',
        loadChildren: () => import('../../business/mp-update-external/mp-update-external.module').then((m) => m.MpUpdateExternalModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "PAYMENT_UPDATE_AN_EXTERNAL_RECIPIENT"],
      },
      {
        path: 'manage-payees/add-domestic-payees',
        loadChildren: () => import('../../business/mp-add-domestic/mp-add-domestic.module').then((m) => m.MpAddDomesticModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "ADD_DOMESTIC_PAYEE"],
      },
      {
        path: 'manage-payees/delete-domestic-payees',
        loadChildren: () => import('../../business/mp-delete-domestic/mp-delete-domestic.module').then((m) => m.MpDeleteDomesticModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "DELETE_DOMESTIC_PAYEES"],
      },
      {
        path: 'manage-payees/update-domestic-payees',
        loadChildren: () => import('../../business/mp-update-domestic/mp-update-domestic.module').then((m) => m.MpUpdateDomesticModule),
        data: ["PAYMENTS", "PAYMENT_MANAGE_PAYEES", "UPDATE_DOMESTIC_PAYEE"],
      },
      {
        path: 'mobile-cheque-deposit',
        loadChildren: () => import('../../business/mobile-cheque-deposit/mobile-cheque-deposit.module').then((m) => m.MobileChequeDepositModule),
        data: []
      },
      {
        path: 'mobile-queue',
        loadChildren: () => import('../../business/mobile-queue/mobile-queue.module').then((m) => m.MobileQueueModule),
      },
      {
        path: 'order-history',
        loadChildren: () => import('../../business/order-history/order-history.module').then((m) => m.OrderHistoryModule),
        data: ["ORDERS", "ORDERS_HISTORY"],
      },
      {
        path: 'payment/domestic',
        loadChildren: () => import('../../business/payment-domestic/payment-domestic.module').then((m) => m.PaymentDomesticModule),
        data: ["PAYMENTS", "DOMESTIC_PAYMENT"],
      },
      {
        path: 'payment/external',
        loadChildren: () => import('../../business/payment-external/payment-external.module').then((m) => m.PaymentExternalModule),
        data: ["PAYMENTS", "PAYMENT_EXTERNAL_PAYMENT"],
      },
      {
        path: 'payment/general',
        loadChildren: () => import('../../business/payment-general/payment-general.module').then((m) => m.PaymentGeneralModule),
        data: ["PAYMENTS", "LABEL_MAKE_A_PAYMENT"],
      },
      {
        path: 'payment-history',
        loadChildren: () => import('../../business/payment-history/payment-history.module').then((m) => m.PaymentHistoryModule),
        data: ["PAYMENTS", "PAYMENT_HISTORY"],
      },
      {
        path: 'payment/report',
        loadChildren: () => import('../../business/payment-reports/payment-reports.module').then((m) => m.PaymentReportsModule),
        data: ["PAYMENTS", "LABEL_MOBILEARTH_TOPUP_REPORTS"]
      },
      {
        path: 'payment/topup',
        loadChildren: () => import('../../business/payment-topup/payment-topup.module').then((m) => m.PaymentTopupModule),
        data: ["PAYMENTS", "LABEL_MOBILEARTH_TOPUP_PAYMENT"],
      },
      {
        path: 'payroll-batch-upload',
        loadChildren: () => import('../../business/payroll-batch-upload/payroll-batch-upload.module').then((m) => m.PayrollBatchUploadModule),
        data: ["PAYROLL", "PAYROLL_UPLOAD_TITLE"],
      },
      {
        path: 'payroll-department',
        loadChildren: () => import('../../business/payroll-department/payroll-department.module').then((m) => m.PayrollDepartmentModule),
        data: ["PAYROLL", "PAYROLL_DEPARTMENTS_TITLE"],
      },
      {
        path: 'payroll-department-group',
        loadChildren: () => import('../../business/payroll-department-group/payroll-department-group.module').then((m) => m.PayrollDepartmentGroupModule),
        data: ["PAYROLL", "PAYROLL_DEPARTMENT_GROUPS_TITLE"],
      },
      {
        path: 'payroll-manage-batches',
        loadChildren: () => import('../../business/payroll-manage-batches/payroll-manage-batches.module').then((m) => m.PayrollManageBatchesModule),
        data: ["PAYROLL", "PAYROLL_DEPARTMENTS_MANAGE_BATCHES"],
      },
      {
        path: 'payroll-manage-members',
        loadChildren: () => import('../../business/payroll-manage-members/payroll-manage-members.module').then((m) => m.PayrollManageMembersModule),
        data: ["PAYROLL", "PAYROLL_DEPARTMENTS_MANAGE_MEMBERS"],
      },
      {
        path: 'pending-transactions',
        loadChildren: () => import('../../business/pending-transactions/pending-transactions.module').then((m) => m.PendingTransactionsModule),
        data: ["LABEL_ACCOUNTS", "TRANSACTION_PENDING_TITLE"],
      },
      {
        path: 'pending-signature',
        loadChildren: () => import('../../business/pending-signature/pending-signature.module').then((m) => m.PendingSignatureModule),
        data: [],
      },
      {
        path: 'personal-finance-management',
        loadChildren: () => import('../../business/personal-finance-management/personal-finance-management.module').then((m) => m.PersonalFinanceManagementModule),
      },
      {
        path: 'personal-finance-management-landing',
        loadChildren: () => import('../../business/personal-finance-management-landing/personal-finance-management-landing.module').then((m) => m.PersonalFinanceManagementLandingModule),
      },
      {
        path: 'personal-finance-offers',
        loadChildren: () => import('../../business/personal-finance-offers/personal-finance-offers.module').then((m) => m.PersonalFinanceOffersModule),
      },
      {
        path: 'qr-create',
        loadChildren: () => import('../../business/qr-create/qr-create.module').then((m) => m.QrCreateModule),
        data: ["QRCODES.MAIN_TITLE", "QRCODES.REQUEST"]
      },
      {
        path: 'qr-main',
        loadChildren: () => import('../../business/qr-main/qr-main.module').then((m) => m.QrMainModule),
        data: ["QRCODES.MAIN_TITLE"]
      },
      {
        path: 'qr-payment',
        loadChildren: () => import('../../business/qr-payment/qr-payment.module').then((m) => m.QrPaymentModule),
        data: ["QRCODES.MAIN_TITLE", "QRCODES.PAYMENT_BREADCRUMB"]
      },
      {
        path: 'resource-viewer',
        loadChildren: () => import('../../business/resource-viewer/resource-viewer.module').then((m) => m.ResourceViewerModule),
        data: ["LABEL_SUPPORT_CENTER", "SUPPORT_CENTER.TITLE", "SUPPORT_CENTER.RESOURCES"]
      },
      {
        path: 'scheduled-orders',
        loadChildren: () => import('../../business/scheduled-orders/scheduled-orders.module').then((m) => m.ScheduledOrdersModule),
        data: ["ORDERS", "SCHEDULED_ORDERS.SCHEDULED_ORDERS_TITLE"],
      },
      {
        path: 'scheduled-transactions',
        loadChildren: () => import('../../business/scheduled-transactions/scheduled-transactions.module').then((m) => m.ScheduledTransactionsModule),
        data: ["LABEL_ACCOUNTS", "SCHEDULED_TRANSACTIONS.SCHEDULED_TRANSACTIONS_TITLE"],
      },
      {
        path: 'secure-message',
        loadChildren: () => import('../../business/secure-message/secure-message.module').then((m) => m.SecureMessageModule),
        data: ["LABEL_SERVICES", "SECURE_MESSAGE.SECURE_MESSAGE_TITLE"],
      },
      {
        path: 'security-question',
        loadChildren: () => import('../../business/security-question/security-question.module').then((m) => m.SecurityQuestionModule),
      },
      {
        path: 'services/applications',
        loadChildren: () => import('../../business/application-form/application-form.module').then((m) => m.ApplicationFormModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS"],
      },
      {
        path: 'services/applications-create',
        loadChildren: () => import('../../business/application-form-create/application-form-create.module').then((m) => m.ApplicationFormCreateModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/new-account',
        loadChildren: () => import('../../business/application-form-new-account/application-form-new-account.module').then((m) => m.ApplicationFormNewAccountModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/personal-loan',
        loadChildren: () => import('../../business/application-form-personal-loan/application-form-personal-loan.module').then((m) => m.ApplicationFormPersonalLoanModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/self-registration',
        loadChildren: () => import('../../business/application-form-self-registration/application-form-self-registration.module').then((m) => m.ApplicationFormSelfRegistrationModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/car-second',
        loadChildren: () => import('../../business/application-form-car-second/application-form-car-second.module').then((m) => m.ApplicationFormCarSecondModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/mortgage-cash',
        loadChildren: () => import('../../business/application-form-mortgage-cash/application-form-mortgage-cash.module').then((m) => m.ApplicationFormMortgageCashModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/emergency-psa',
        loadChildren: () => import('../../business/application-form-emergency-psa/application-form-emergency-psa.module').then((m) => m.ApplicationFormEmergencyPsaModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/applications/personal-school',
        loadChildren: () => import('../../business/application-form-personal-school/application-form-personal-school.module').then((m) => m.ApplicationFormPersonalSchoolModule),
        data: ["LABEL_SERVICES", "LITE_APPLICATIONS.APPLICATIONS", "LITE_APPLICATIONS.NEW_APPLICATIONS"],
      },
      {
        path: 'services/card',
        loadChildren: () => import('../../business/services-card/services-card.module').then((m) => m.ServicesCardModule),
        data: ["LABEL_SERVICES", "CARD"],
      },
      {
        path: 'services/card/change-address',
        loadChildren: () => import('../../business/card-change-address/card-change-address.module').then((m) => m.CardChangeAddressModule),
        data: ["LABEL_SERVICES", "CARD", "CHANGE_ADDRESS.LABEL_CHANGE_ADDRESS"],
      },
      {
        path: 'services/card/report-lost',
        loadChildren: () => import('../../business/card-report-lost/card-report-lost.module').then((m) => m.CardReportLostModule),
        data: ["LABEL_SERVICES", "CARD", "REPORT_LOST.LABEL_REPORT_LOST"],
      },
      {
        path: 'services/card/manage-debit-card',
        loadChildren: () => import('../../business/manage-debit-card/manage-debit-card.module').then((m) => m.ManageDebitCardModule),
        data: ["LABEL_SERVICES", "CARD", "SERVICE_CARD.MANAGE_DEBIT_CARD"],
      },
      {
        path: 'services/card/debit-card-reset-pin',
        loadChildren: () => import('../../business/debit-card-reset-pin/debit-card-reset-pin.module').then((m) => m.DebitCardResetPinModule),
        data: ["LABEL_SERVICES", "CARD", "SERVICE_CARD.MANAGE_DEBIT_CARD"],
      },
      {
        path: 'services/card/debit-card-change-pin',
        loadChildren: () => import('../../business/debit-card-change-pin/debit-card-change-pin.module').then((m) => m.DebitCardChangePinModule),
        data: ["LABEL_SERVICES", "CARD", "SERVICE_CARD.MANAGE_DEBIT_CARD"],
      },
      {
        path: 'services/card/manage-credit-card',
        loadChildren: () => import('../../business/manage-credit-card/manage-credit-card.module').then((m) => m.ManageCreditCardModule),
        data: ["LABEL_SERVICES", "CARD", "SERVICE_CARD.MANAGE_CREDIT_CARD"],
      },
      {
        path: 'services/card/credit-card-reset-pin',
        loadChildren: () => import('../../business/credit-card-reset-pin/credit-card-reset-pin.module').then((m) => m.CreditCardResetPinModule),
        data: ["LABEL_SERVICES", "CARD", "SERVICE_CARD.MANAGE_CREDIT_CARD"],
      },
      {
        path: 'services/card/credit-card-change-pin',
        loadChildren: () => import('../../business/credit-card-change-pin/credit-card-change-pin.module').then((m) => m.CreditCardChangePinModule),
        data: ["LABEL_SERVICES", "CARD", "SERVICE_CARD.MANAGE_CREDIT_CARD"],
      },
      {
        path: 'services/multi-card/manage-multi-card',
        loadChildren: () => import('../../business/manage-multi-card/manage-multi-card.module').then((m) => m.ManageMultiCardModule),
        data: ["LABEL_SERVICES", "CARD"]
      },
      {
        path: 'services/request',
        loadChildren: () => import('../../business/services-request/services-request.module').then((m) => m.ServicesRequestModule),
        data: ["LABEL_SERVICES", "SERVICE_REQUEST.SERVICE_REQUEST_LABEL"],
      },
      {
        path: 'services/smart-forms',
        loadChildren: () => import('../../business/smart-forms/smart-forms.module').then((m) => m.SmartFormsModule),
        data: ["LABEL_SERVICES", "SMART_FORMS.MENU"],
      },
      {
        path: 'settings/admin',
        loadChildren: () => import('../../business/settings-admin/settings-admin.module').then((m) => m.SettingsAdminModule),
        data: ["LABEL_SETTINGS", "SETTINGS_ADMIN"],
      },
      {
        path: 'settings/admin/manage-payees',
        loadChildren: () => import('../../business/settings-admin-manage-payees/settings-admin-manage-payees.module').then((m) => m.SettingsAdminManagePayeesModule),
        data: ["LABEL_SETTINGS", "SETTINGS_ADMIN", "PAYMENT_MANAGE_PAYEES"],
      },
      {
        path: 'settings/admin/manage-payees/delete-merchants',
        loadChildren: () => import('../../business/settings-admin-manage-payees/sa-mp-delete-merchants/sa-mp-delete-merchants.module').then((m) => m.SaMpDeleteMerchantsModule),
        data: ["LABEL_SETTINGS", "SETTINGS_ADMIN", "PAYMENT_MANAGE_PAYEES", "PAYMENT_DELETE_A_MERCHANT"],
      },
      {
        path: 'settings/admin/manage-payees/add-merchants',
        loadChildren: () => import('../../business/settings-admin-manage-payees/sa-mp-add-merchants/sa-mp-add-merchants.module').then((m) => m.SaMpAddMerchantsModule),
        data: ["LABEL_SETTINGS", "SETTINGS_ADMIN", "PAYMENT_MANAGE_PAYEES", "PAYMENT_ADD_A_MERCHANT"],
      },
      {
        path: 'settings/admin/manage-payees/update-merchants',
        loadChildren: () => import('../../business/settings-admin-manage-payees/sa-mp-update-merchants/sa-mp-update-merchants.module').then((m) => m.SaMpUpdateMerchantsModule),
        data: ["LABEL_SETTINGS", "SETTINGS_ADMIN", "PAYMENT_MANAGE_PAYEES", "PAYMENT_UPDATE_MERCHANT"],
      },
      {
        path: 'settings/alerts',
        loadChildren: () => import('../../business/settings-alerts/settings-alerts.module').then((m) => m.SettingsAlertsModule),
        data: ["LABEL_SETTINGS", "LABEL_ALERTS"],
      },
      {
        path: 'settings/personal',
        loadChildren: () => import('../../business/settings-personal/settings-personal.module').then((m) => m.SettingsPersonalModule),
        data: ["LABEL_SETTINGS", "SETTINGS_PERSONAL"],
      },
      {
        path: 'settings/personal/alias',
        loadChildren: () => import('../../business/personal-alias/personal-alias.module').then((m) => m.MePersonalAliasModule),
        data: ["LABEL_SETTINGS", "LABEL_ALIAS"],
      },  
      {
        path: 'settings/personal/changeEmail',
        loadChildren: () => import('../../business/personal-change-email/personal-change-email.module').then((m) => m.PersonalChangeEmailModule),
        data: ["LABEL_SETTINGS", "LABEL_EMAIL"],
      },
      {
        path: 'settings/personal/changePassword',
        loadChildren: () => import('../../business/personal-change-password/personal-change-password.module').then((m) => m.PersonalChangePasswordModule),
        data: ["LABEL_SETTINGS", "CHANGE_PASSWORD.CHANGE_YOUR_PASSWORD"],
      },
      {
        path: 'settings/personal/changeSecurityQuestion',
        loadChildren: () => import('../../business/personal-security-question/personal-security-question.module').then((m) => m.PersonalSecurityQuestionModule),
        data: ["LABEL_SETTINGS", "SECURITY_QUESTION_CREATE.SECURITY_QUESTIONS"]
      },
      {
        path: 'settings/personal/mobileId',
        loadChildren: () => import('../../business/personal-mobile-id/personal-mobile-id.module').then((m) => m.PersonalMobileIdModule),
        data: ["LABEL_SETTINGS", "LABEL_MOBILE_ID"],
      },
      {
        path: 'settings/personal/viz-alias',
        loadChildren: () => import('../../business/personal-viz-alias/personal-viz-alias.module').then((m) => m.PersonalVizAliasModule),
        data: ["LABEL_SETTINGS", "VIZ.ALIAS"],
      },
      {
        path: 'settings/preference',
        loadChildren: () => import('../../business/settings-preference/settings-preference.module').then((m) => m.SettingsPreferenceModule),
        data: ["LABEL_SETTINGS", "LABEL_PREFERENCES"],
      },
      {
        path: 'settings/preference/timezone',
        loadChildren: () => import('../../business/preference-timezone/preference-timezone.module').then((m) => m.PreferenceTimezoneModule),
        data: ["LABEL_SETTINGS", "LABEL_PREFERENCES", "LABEL_TIME_ZONE"],
      },
      {
        path: 'settings/preference/marketing',
        loadChildren: () => import('../../business/preference-marketing/preference-marketing.module').then((m) => m.PreferenceMarketingModule),
        data: ["LABEL_SETTINGS", "LABEL_PREFERENCES", "LABEL_MARKETING"],
      },
      {
        path: 'settings/preference/text-message',
        loadChildren: () => import('../../business/preference-text-message/preference-text-message.module').then((m) => m.PreferenceTextMessageModule),
        data: ["LABEL_SETTINGS", "LABEL_PREFERENCES", "LABEL_TEXT_MESSAGE"],
      },
      {
        path: 'settings/preference/language',
        loadChildren: () => import('../../business/preference-language/preference-language.module').then((m) => m.PreferenceLanguageModule),
        data: ["LABEL_SETTINGS", "LABEL_PREFERENCES", "BUTTON_LANGUAGE"],
      },
      {
        path: 'settings/reset-push',
        loadChildren: () => import('../../business/settings-reset-push/settings-reset-push.module').then((m) => m.SettingsResetPushModule),
        data: ["LABEL_SETTINGS", "RESET_PUSH_TOKENS.TITLE"],
      },
      {
        path: 'stop-payment',
        loadChildren: () => import('../../business/stop-payment/stop-payment.module').then((m) => m.StopPaymentModule),
        data: ["LABEL_SERVICES", "STOP_PAYMENT.STOP_PAYMENT_TITLE"],
      },
      {
        path: 'support-center',
        loadChildren: () => import('../../business/support-center/support-center.module').then((m) => m.SupportCenterModule),
        data: ["LABEL_SUPPORT_CENTER", "SUPPORT_CENTER.TITLE"]
      },
      {
        path: 'templates',
        loadChildren: () => import('../../business/order-template/order-template.module').then((me) => me.OrderTemplateModule),
        data: ["ORDERS", "ORDERS_TEMPLATES.TITLE"]
      },
      {
        path: 'templates/create',
        loadChildren: () => import('../../business/order-template-create/order-template-create.module').then((me) => me.OrderTemplateCreateModule),
        data: ["ORDERS", "ORDERS_TEMPLATES.TITLE", "BUTTON_CREATE"]
      },
      {
        path: 'templates/edit',
        loadChildren: () => import('../../business/order-template-update/order-template-update.module').then((me) => me.OrderTemplateUpdateModule),
        data: ["ORDERS", "ORDERS_TEMPLATES.TITLE", "BUTTON_UPDATE"]
      },
      {
        path: 'terms-and-conditions',
        loadChildren: () => import('../../business/terms-and-condition/terms-and-condition.module').then((m) => m.TermsAndConditionModule),
      },
      {
        path: 'tools',
        loadChildren: () => import('../../business/tools/tools.module').then((m) => m.ToolsModule),
        data: ["LABEL_SUPPORT_CENTER", "SUPPORT_CENTER.TITLE", "SUPPORT_CENTER.TOOLS"]
      },
      {
        path: 'transfers-by-text',
        loadChildren: () => import('../../business/transfers-by-text/transfers-by-text.module').then((m) => m.TransfersByTextModule),
        data: ["LABEL_TRANSFERS", "LABEL_TRANSFER_BY_TEXT"],
      },
      {
        path: 'transfers-by-text-add',
        loadChildren: () => import('../../business/transfers-by-text-add/transfers-by-text-add.module').then((m) => m.TransfersByTextAddModule),
        data: ["LABEL_TRANSFERS", "LABEL_TRANSFER_BY_TEXT"],
      },
      {
        path: 'transfers-by-text-update',
        loadChildren: () => import('../../business/transfers-by-text-update/transfers-by-text-update.module').then((m) => m.TransfersByTextUpdateModule),
        data: ["LABEL_TRANSFERS", "LABEL_TRANSFER_BY_TEXT"],
      },
      {
        path: 'view-maintenance',
        loadChildren: () => import('../../business/view-maintenance/view-maintenance.module').then((m) => m.ViewMaintenanceModule),
        data: ["LABEL_ACCOUNTS", "LABEL_VIEW_MAINTENANCE"],
      },
      {
        path: 'viz-history',
        loadChildren: () => import('../../business/viz-history/viz-history.module').then((m) => m.VizHistoryModule),
        data: ["LABEL_EXTERNAL_TRANSFERS", "VIZ.SEND_MONEY", "VIZ.HISTORY"],
      },
      {
        path: 'viz-payee-add',
        loadChildren: () => import('../../business/viz-payee-add/viz-payee-add.module').then((m) => m.VizPayeeAddModule),
        data: ["LABEL_EXTERNAL_TRANSFERS", "VIZ.SEND_MONEY", "VIZ.ADD_PAYEE"],
      },
      {
        path: 'viz-payee-delete',
        loadChildren: () => import('../../business/viz-payee-delete/viz-payee-delete.module').then((m) => m.VizPayeeDeleteModule),
        data: ["LABEL_EXTERNAL_TRANSFERS", "VIZ.SEND_MONEY", "VIZ.DELETE_A_PAYEE"],
      },
      {
        path: 'viz-payee-update',
        loadChildren: () => import('../../business/viz-payee-update/viz-payee-update.module').then((m) => m.VizPayeeUpdateModule),
        data: ["LABEL_EXTERNAL_TRANSFERS", "VIZ.SEND_MONEY", "VIZ.UPDATE_PAYEE"],
      },
      {
        path: 'viz-send-money',
        loadChildren: () => import('../../business/viz-send-money/viz-send-money.module').then((m) => m.VizSendMoneyModule),
        data: ["LABEL_EXTERNAL_TRANSFERS", "VIZ.SEND_MONEY"],
      },
      {
        path: 'viz-settings',
        loadChildren: () => import('../../business/viz-settings/viz-settings.module').then((m) => m.VizSettingsModule),
        data: ["LABEL_EXTERNAL_TRANSFERS", "VIZ.SEND_MONEY", "VIZ.SETTINGS"],
      },
      {
        path: 'voice-command',
        loadChildren: () => import('../../business/voice-command/voice-command.module').then((m) => m.VoiceCommandModule),
      },
      {
        path: 'wire',
        loadChildren: () => import('../../business/wire-transfer/wire-transfer.module').then((me) => me.WireTransferModule),
        data: ["ORDERS", "WIRE_TRANSFER"]
      },
    ],
  },
];